import languages from '@pp/languages';

import type { DictionaryMap, DictionaryTags } from '../services/dictionary/types';

import { capitalize } from './capitalize';

export interface ITranslationConfig {
  /** Provide codes of languages not supported by your product. Keep null if non-applicable. List of all languages: /libraries/languages */
  excludedLanguages: string[] | null;
  /** Provide Lokalise tags. E.g., 'core', 'table_name', 'promo', 'topcard' */
  uiFiles: DictionaryTags;
  /** Optional. A map of keys to keys. E.g., 'BALANCE' -> 'CREDIT'. */
  uiDictionaryMap?: DictionaryMap;
  /** Provide Lokalise help file names. E.g., 'common', 'topcard'. */
  helpFiles?: DictionaryTags;
  /** Optional. A map of keys to keys. E.g., 'BALANCE' -> 'CREDIT'. */
  helpDictionaryMap?: DictionaryMap;
}

export const DEFAULT_LANGUAGE_CODE = languages.default; // "en"

/**
 * Returns parsed language from browser's locale which are in format "de-DE", "ja-JP", "zh-Hans-CN", etc.
 */
export const parseBrowserLocale = (locale: string): string => locale.split('-')[0] ?? locale;

/**
 * Determines if language code is among supported localisations.
 */
const isLanguageAvailable = (languageCode: string, excludedLanguages: string[]): boolean => {
  return languageCode in languages.available && !excludedLanguages.includes(languageCode);
};

const languageCodeMap: Record<string, string> = {
  soce: 'en_soce-v1',
};

const resolveLanguageCode = (languageCode?: string): string | undefined => {
  if (!languageCode) return;
  return languageCodeMap[languageCode] ?? languageCode;
};

/**
 * Retrieves the language based on the provided configuration and session language code.
 * If localisation is not supported, it falls back to browser's language, then to default language.
 */
export const getLanguage = (
  translationConfig: ITranslationConfig,
  sessionLanguageCode: string | undefined
): {
  locale: string;
  handle: string;
} => {
  const sessionLanguageLowerCase = resolveLanguageCode(sessionLanguageCode?.toLowerCase());
  const sessionLocale = sessionLanguageLowerCase?.split('_')[0];

  const excludedLanguages = translationConfig.excludedLanguages || [];

  // Check if the session language code is available and not excluded
  if (sessionLocale && isLanguageAvailable(sessionLocale, excludedLanguages)) {
    return { locale: sessionLocale, handle: sessionLanguageLowerCase };
  }

  // Fallback to the browser's language
  const browserLanguage = parseBrowserLocale(navigator.language);

  if (browserLanguage && isLanguageAvailable(browserLanguage, excludedLanguages)) {
    return { locale: browserLanguage, handle: browserLanguage };
  }

  // Fallback to the default language
  return { locale: DEFAULT_LANGUAGE_CODE, handle: DEFAULT_LANGUAGE_CODE };
};

/**
 * Determines if the given language code corresponds to a Right-to-Left (RTL) language.
 */
export const getIsLanguageRTL = (languageCode: string): boolean => languages.rtl.includes(languageCode);

/**
 * Based on "@pp/languages": "../libraries/languages",
 */
const LANGUAGE_CODE_MAP = {
  cn: 'zh', // Chinese (Simplified)
  nf: 'nl-BE', // Flemish (Belgian Dutch)
  // All other codes are already valid BCP 47 codes
};

/**
 * Returns the name of the language in "locale" language.
 */
export const getLanguageName = (languageCode: string, locale: string): string => {
  const code = LANGUAGE_CODE_MAP[languageCode as keyof typeof LANGUAGE_CODE_MAP];
  try {
    return capitalize(new Intl.DisplayNames([locale], { type: 'language' }).of(code ?? languageCode) ?? languageCode);
  } catch (_) {
    return capitalize(new Intl.DisplayNames(['en'], { type: 'language' }).of(code ?? languageCode) ?? languageCode);
  }
};

export const getLanguageSelfName = (languageCode: string): string => {
  if (languageCode in languages.languagesSelfNames) {
    return languages.languagesSelfNames[languageCode as keyof typeof languages.languagesSelfNames];
  }
  // fallback to Intl.DisplayNames API
  return getLanguageName(languageCode, languageCode);
};
